import { createContext, useContext, useEffect, useState } from 'react';
import { ApiError, authApi, setActiveTenantId, type TenantSummary } from './api/client';

interface User {
  id: string | null;
  email: string;
  name: string | null;
}

interface AuthContextValue {
  user: User | null;
  tenants: TenantSummary[];
  activeTenantId: string | null;
  activeTenantRole: 'owner' | 'viewer';
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  /** Switch the active tenant; reloads so every query refetches under it */
  switchTenant: (tenantId: string) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// Mock user for local development
const DEV_USER: User = {
  id: 'dev-user-1',
  email: 'dev@localhost',
  name: 'Dev User',
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [tenants, setTenants] = useState<TenantSummary[]>([]);
  const [activeTenantId, setActiveTenant] = useState<string | null>(null);
  const [activeTenantRole, setActiveTenantRole] = useState<'owner' | 'viewer'>('owner');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchUser = async () => {
    // In development, use mock user
    if (import.meta.env.DEV) {
      setUser(DEV_USER);
      setTenants([{ id: 'dev-user-1', email: DEV_USER.email, name: DEV_USER.name, role: 'owner' }]);
      setActiveTenant('dev-user-1');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const data = await authApi.me();
      setUser(data.user);
      setTenants(data.tenants);
      setActiveTenant(data.activeTenantId);
      setActiveTenantRole(data.activeTenantRole);
    } catch (err) {
      // A stored tenant selection the server no longer accepts (e.g. access
      // revoked) would fail every request — clear it and start over
      if (err instanceof ApiError && err.message === 'Unknown tenant') {
        setActiveTenantId(null);
        window.location.reload();
        return;
      }
      setUser(null);
      if (err instanceof Error && !err.message.includes('Unauthorized')) {
        setError(err);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUser();
  }, []);

  const switchTenant = (tenantId: string) => {
    setActiveTenantId(tenantId);
    // Full reload: every cached query belongs to the previous tenant
    window.location.reload();
  };

  return (
    <AuthContext.Provider
      value={{ user, tenants, activeTenantId, activeTenantRole, isLoading, error, refetch: fetchUser, switchTenant }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
